import React, { Component } from 'react';
import { string, bool, func } from 'prop-types';

import { without, maxBy } from 'lodash-es';
import { LocalizedInput } from '../../../../LocalizedInput';
import { getTranslation, withLang, langType } from '../../../../../i18n';
import { SurveySchemaPartItemEditorType } from '../../../../../constants/types';
import styles from './selectionQuestionEditor.module.css';

class SelectionQuestionEditor extends Component {
  constructor(props) {
    super(props);

    this.onQuestionInputChange = this.onQuestionInputChange.bind(this);
    this.onOptionInputChange = this.onOptionInputChange.bind(this);
    this.addOption = this.addOption.bind(this);
    this.removeOption = this.removeOption.bind(this);
    this.onValueChange = this.onValueChange.bind(this);
    this.namespaceKey = this.namespaceKey.bind(this);

    this.state = {
      itemIndex: this.props.item.order - 1,
      openOptionIndexes: [0, 1],
      questionOpen: true
    };
  }

  onQuestionInputChange(lang) {
    return (event) => this.props.dispatchUpdateItem(this.state.itemIndex, {
      question: {
        [event.target.name]: { [lang]: event.target.value }
      }
    });
  }

  onOptionInputChange(index) {
    return (lang) => (event) => {
      this.props.item.question.responseOptions[index].text[lang] = event.target.value;

      this.props.dispatchUpdateItem(this.state.itemIndex, {
        question: {
          responseOptions: this.props.item.question.responseOptions
        }
      });
    };
  }

  onValueChange(index) {
    return (event) => {
      this.props.item.question.responseOptions[index].value = event.target.value;

      this.props.dispatchUpdateItem(this.state.itemIndex, {
        question: {
          value: this.props.item.question.value
        }
      });
    };
  }

  addOption() {
    const { responseOptions } = this.props.item.question;
    const numberValues = responseOptions.filter((option) => !Number.isNaN(option.value));
    const getMaxNumberValue = () => Number(maxBy(numberValues, 'value').value);
    const value = numberValues.length > 0 ? getMaxNumberValue() + 1 : 1;
    const emptyOption = { value, text: {} };
    const nextOptions = [...responseOptions, emptyOption];

    this.props.dispatchUpdateItem(this.state.itemIndex, {
      question: {
        responseOptions: nextOptions
      }
    });
    this.setState({
      openOptionIndexes: [nextOptions.length - 1],
      questionOpen: true
    });
  }

  removeOption(index) {
    const { responseOptions } = this.props.item.question;
    const optionToRemove = responseOptions[index];

    this.props.dispatchUpdateItem(this.state.itemIndex, {
      question: {
        responseOptions: without(responseOptions, optionToRemove)
      }
    });
  }

  namespaceKey(str) {
    return this.props.translationNamespace.concat(str);
  }

  render() {
    const t = this.props.translate;
    const renderOptionEditor = (option, index) => (
      <div key={index}>
        <div className={styles.optionHeader}>
          <p>{t('common.value')}:
            <span>
              <div className={styles.value}>
                <input
                  type="number"
                  value={option.value}
                  onChange={this.onValueChange(index)}
                  disabled={this.props.lockedFromEditing}
                />
              </div>
              <button
                type="button"
                className="actionButton icon--trash iconRemove"
                onClick={() => this.removeOption(index)}
                data-cy="survey-schema-part-remove-option-button"
                title={t('common.remove')}
                disabled={this.props.lockedFromEditing}
              />
            </span>
          </p>
        </div>
        <div className={`${styles.option} ${styles.openEditor}`}>
          <div className={styles.number}>
            <div className={styles.editorLabel}>
              <p>{index + 1}.</p>
            </div>
          </div>
          <div className={styles.texts}>
            <LocalizedInput
              placeholderTranslationNamespace={this.namespaceKey('.option')}
              onInputChange={this.onOptionInputChange(index)}
              values={option.text}
              name="text"
              maxLength={255}
              dataCyPrefix="survey-schema-part-option-input"
            />
          </div>
        </div>
      </div>
    );

    const renderOptionText = (option, index) => (
      <div key={index} className={styles.option}>
        <div className={styles.number}>
          <p>{index + 1}</p>
        </div>
        <div className={styles.texts}>
          {
            getTranslation(option.text, this.props.lang)
              ? (<span>{getTranslation(option.text, this.props.lang)}</span>)
              : (<span className={styles.emptyOption}>{t('surveyParts.responseOption.empty')}</span>)
          }
          <button
            type="button"
            className={`${styles.editButton} icon--edit-event`}
            onClick={() => this.setState({ openOptionIndexes: [index] })}
          />
        </div>
      </div>
    );

    const renderOption = (option, index) =>
      (this.state.openOptionIndexes.includes(index)
        ? renderOptionEditor(option, index) : renderOptionText(option, index));

    const questionTextInput = (
      <LocalizedInput
        placeholderTranslationNamespace={this.namespaceKey('.question')}
        onInputChange={this.onQuestionInputChange}
        values={this.props.item.question.text}
        name="text"
        maxLength={255}
        dataCyPrefix="survey-schema-part-radio-button-group-question-input"
      />
    );

    const questionText = (
      <div className={`${styles.texts} ${styles.closedQuestion}`}>
        <p>{getTranslation(this.props.item.question.text, this.props.lang)}</p>
        <button
          type="button"
          className={`${styles.editButton} icon--edit-event`}
          onClick={() => this.setState({ questionOpen: true })}
        />
      </div>
    );

    return (
      <div>
        {this.state.questionOpen ? questionTextInput : questionText}
        <div>
          {this.props.item.question.responseOptions.map(renderOption)}
        </div>
        {!this.props.lockedFromEditing
          && (
          <div className={styles.buttonContainer}>
            <button
              type="button"
              onClick={this.addOption}
              className="button--action icon--plus"
              data-cy="survey-schema-part-add-option-button"
            >
              {t(this.namespaceKey('.addOption'))}
            </button>
          </div>
          )}
      </div>
    );
  }
}

SelectionQuestionEditor.propTypes = {
  item: SurveySchemaPartItemEditorType.isRequired,
  translationNamespace: string.isRequired,
  lockedFromEditing: bool.isRequired,
  dispatchUpdateItem: func.isRequired,
  ...langType
};

export default withLang(SelectionQuestionEditor);
